import { useCallback, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Layers,
  Play,
  Search,
  Star,
  StarOff,
} from 'lucide-react';
import type { CommandSpec } from '@/lib/types';
import clsx from 'clsx';
import {
  useDevicePackagePrefs,
  useDevicePackagePrefsUpdate,
} from '@/lib/myPrefs';

/* ----------------------- B2 prefs.devices[serial].packages[pkg].control.{pins,collapsed} ----- */

interface ControlPackageCtrl {
  control?: { pins?: unknown; collapsed?: unknown };
}

function asStringSet(raw: unknown): Set<string> {
  if (!Array.isArray(raw)) return new Set();
  return new Set(raw.filter((x): x is string => typeof x === 'string'));
}

/* ----------------------- Required-args probe ----------------------- */

/**
 * Returns true when the command's args schema declares at least one
 * required field. The inline ▷ run button on a list row uses this to
 * decide whether to dispatch the command directly (no required args)
 * or merely select + focus the form (requires user input).
 */
function specHasRequiredArgs(spec: CommandSpec): boolean {
  const required = spec.args.schema?.required ?? [];
  return Array.isArray(required) && required.length > 0;
}

/* --------------------------- Component --------------------------- */

interface Props {
  /** Catalog list to render. */
  commands: CommandSpec[];
  /** Currently selected command name. */
  activeName?: string;
  /** Called when the user clicks a row to select it. */
  onSelect: (name: string) => void;
  /**
   * Called when the user hits the inline ▷ Run button. The list
   * itself only dispatches commands with zero required args; for
   * commands with required args it falls back to onSelect.
   */
  onRun: (spec: CommandSpec) => void;
  /** Active device serial — used to scope pin/collapse storage. */
  serial: string;
  /** Active package — used to scope pin/collapse storage. */
  package: string;
  /** True while the catalog request is in-flight. */
  loading?: boolean;
  /** Catalog error to surface. */
  error?: Error;
}

export function ControlCommandList({
  commands,
  activeName,
  onSelect,
  onRun,
  serial,
  package: pkg,
  loading,
  error,
}: Props): JSX.Element {
  const [query, setQuery] = useState('');
  const pkgPrefs = useDevicePackagePrefs<ControlPackageCtrl>(serial, pkg);
  const updatePkg = useDevicePackagePrefsUpdate(serial, pkg);

  const pins = useMemo(() => asStringSet(pkgPrefs.control?.pins), [pkgPrefs]);
  const collapsed = useMemo(() => asStringSet(pkgPrefs.control?.collapsed), [pkgPrefs]);

  const togglePin = useCallback(
    (name: string) => {
      const next = new Set(pins);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      updatePkg({ control: { pins: [...next] } });
    },
    [pins, updatePkg],
  );

  const toggleCollapse = useCallback(
    (ns: string) => {
      const next = new Set(collapsed);
      if (next.has(ns)) next.delete(ns);
      else next.add(ns);
      updatePkg({ control: { collapsed: [...next] } });
    },
    [collapsed, updatePkg],
  );

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Match only the canonical wire `name` (e.g. `biz.cart.refresh`) and
    // the user-facing `displayName`. Descriptions are intentionally
    // excluded — they're long sentences that produce noisy partial hits
    // (typing "set" matched every command whose desc contained the
    // word). The narrower match keeps the list scannable.
    const matches = (c: CommandSpec): boolean => {
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        (c.displayName ?? '').toLowerCase().includes(q)
      );
    };
    const pinList = commands.filter((c) => pins.has(c.name) && matches(c));
    pinList.sort((a, b) => a.name.localeCompare(b.name));

    // SDK-managed builtins (system.*) live in their own sticky fold above
    // every business namespace group. The wire-side `builtin` flag is
    // populated by the device adapter's `replyCatalog`; older devices
    // that haven't shipped v2 wire fall back to namespace heuristics so
    // the SPA doesn't strand them in the alphabetical list.
    const isBuiltin = (c: CommandSpec): boolean =>
      c.builtin === true || c.namespace === 'system';
    const builtinList = commands
      .filter((c) => isBuiltin(c) && matches(c))
      .sort((a, b) => a.name.localeCompare(b.name));

    const byNs = new Map<string, CommandSpec[]>();
    for (const c of commands) {
      if (!matches(c)) continue;
      if (isBuiltin(c)) continue;
      const arr = byNs.get(c.namespace) ?? [];
      arr.push(c);
      byNs.set(c.namespace, arr);
    }
    for (const arr of byNs.values()) arr.sort((a, b) => a.name.localeCompare(b.name));
    const groups = [...byNs.entries()].sort(([a], [b]) => a.localeCompare(b));
    return { pinList, builtinList, groups };
  }, [commands, query, pins]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim" />
        <input
          className="input w-full pl-8"
          placeholder="搜索命令名…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/*
       * The list cap (40vh) sized for the multi-app layout where 2-3
       * collapsible panels can be open at once on the left rail. The
       * surrounding aside has its own `max-h-[calc(100vh-160px)]
       * overflow-y-auto`, so when a single panel is open here this
       * inner scroller is rarely active; when multiple panels are
       * open it bounds each one so they stack naturally.
       */}
      <div className="card max-h-[40vh] overflow-auto p-2 text-sm">
        {loading && <p className="px-2 py-4 text-xs text-ink-muted">Loading catalog…</p>}
        {error && (
          <p className="px-2 py-4 text-xs text-rose-500">{error.message}</p>
        )}
        {!loading && !error && commands.length === 0 && (
          <p className="px-2 py-4 text-xs text-ink-muted">
            该 app 未声明任何命令。检查 device SDK 的 `system.catalog` 注册情况。
          </p>
        )}

        {/* Pinned shortcut row — only when at least one match exists. */}
        {grouped.pinList.length > 0 && (
          <div className="mb-2">
            <p className="flex items-center gap-1 px-2 py-1 text-[11px] uppercase tracking-wide text-amber-500">
              <Star className="h-3 w-3" /> 收藏
            </p>
            {grouped.pinList.map((c) => (
              <CommandRow
                key={`pin:${c.name}`}
                spec={c}
                active={activeName === c.name}
                pinned
                onSelect={() => onSelect(c.name)}
                onRun={() => onRun(c)}
                onTogglePin={() => togglePin(c.name)}
              />
            ))}
          </div>
        )}

        {/*
         * 内置命令 (SDK-managed builtins) — pinned sticky-style group at
         * the top of the namespace list to visually separate them from
         * business namespaces. Uses the same collapse/persist logic as
         * regular ns groups with a synthetic key `__builtin__`.
         */}
        {grouped.builtinList.length > 0 && (() => {
          const ns = '__builtin__';
          const isCollapsed = collapsed.has(ns);
          return (
            <div className="mb-1">
              <button
                type="button"
                className="sticky top-0 z-10 flex w-full items-center gap-1 bg-bg px-1 py-1 text-left text-[11px] uppercase tracking-wide text-sky-500 transition hover:text-sky-400"
                onClick={() => toggleCollapse(ns)}
                title={isCollapsed ? '展开' : '折叠'}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
                <Layers className="h-3 w-3" />
                <span>内置命令</span>
                <span className="ml-1 text-ink-dim">({grouped.builtinList.length})</span>
              </button>
              {!isCollapsed &&
                grouped.builtinList.map((c) => (
                  <CommandRow
                    key={`builtin:${c.name}`}
                    spec={c}
                    active={activeName === c.name}
                    pinned={pins.has(c.name)}
                    onSelect={() => onSelect(c.name)}
                    onRun={() => onRun(c)}
                    onTogglePin={() => togglePin(c.name)}
                  />
                ))}
            </div>
          );
        })()}

        {grouped.groups.map(([ns, arr]) => {
          const isCollapsed = collapsed.has(ns);
          return (
            <div key={ns} className="mb-1">
              <button
                type="button"
                className="flex w-full items-center gap-1 px-1 py-1 text-left text-[11px] uppercase tracking-wide text-ink-dim transition hover:text-ink"
                onClick={() => toggleCollapse(ns)}
                title={isCollapsed ? '展开' : '折叠'}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
                <span>{ns}</span>
                <span className="ml-1 text-ink-dim">({arr.length})</span>
              </button>
              {!isCollapsed &&
                arr.map((c) => (
                  <CommandRow
                    key={c.name}
                    spec={c}
                    active={activeName === c.name}
                    pinned={pins.has(c.name)}
                    onSelect={() => onSelect(c.name)}
                    onRun={() => onRun(c)}
                    onTogglePin={() => togglePin(c.name)}
                  />
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CommandRow({
  spec,
  active,
  pinned,
  onSelect,
  onRun,
  onTogglePin,
}: {
  spec: CommandSpec;
  active: boolean;
  pinned: boolean;
  onSelect: () => void;
  onRun: () => void;
  onTogglePin: () => void;
}) {
  const requiresArgs = specHasRequiredArgs(spec);
  return (
    <div
      className={clsx(
        'group flex items-center gap-1 rounded px-1 py-0.5 transition',
        active ? 'bg-accent-soft' : 'hover:bg-bg-soft'
      )}
    >
      <button
        type="button"
        className={clsx(
          'min-w-0 flex-1 px-1 py-1 text-left text-xs transition',
          active ? 'text-accent' : 'text-ink-muted hover:text-ink'
        )}
        onClick={onSelect}
        title={spec.description || spec.displayName}
      >
        <span className="truncate font-mono">{spec.action}</span>
        {spec.displayName && (
          <span className="ml-2 text-ink-dim">{spec.displayName}</span>
        )}
        {spec.subscribe && (
          <span className="ml-1 rounded bg-sky-500/10 px-1 text-[10px] text-sky-500">sub</span>
        )}
      </button>
      <button
        type="button"
        className={clsx(
          'shrink-0 rounded p-1 text-ink-dim opacity-0 transition group-hover:opacity-100',
          'hover:bg-accent-soft hover:text-accent'
        )}
        onClick={onRun}
        title={
          requiresArgs
            ? '该命令有必填参数：先选中并填写表单，再 Run'
            : '一键运行：无必填参数，直接发送'
        }
      >
        <Play className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        className={clsx(
          'shrink-0 rounded p-1 transition',
          pinned
            ? 'text-amber-500 hover:bg-amber-500/10'
            : 'text-ink-dim opacity-0 hover:bg-bg-soft hover:text-amber-500 group-hover:opacity-100'
        )}
        onClick={onTogglePin}
        title={pinned ? '取消收藏' : '收藏到顶部'}
      >
        {pinned ? <Star className="h-3 w-3 fill-current" /> : <StarOff className="h-3 w-3" />}
      </button>
    </div>
  );
}

/** Re-export so Control.tsx can reuse the same helper to decide auto-run. */
export { specHasRequiredArgs };
